import React, { useState, useEffect, useCallback } from 'react';
import {
  billingAPI, creditNotesAPI, dealsAPI, downloadBlob, paymentPlansAPI, quotationsAPI, signingAPI, statementsAPI,
  studentsAPI, usersAPI,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiPlus, FiClock, FiDollarSign, FiUser, FiX, FiTrash2, FiDownload, FiEdit3 } from 'react-icons/fi';
import './Deals.css';

const STAGES = [
  { value: 'NEW', label: 'New Lead' },
  { value: 'QUALIFYING', label: 'Qualifying' },
  { value: 'PROPOSAL_SENT', label: 'Proposal / Quote Sent' },
  { value: 'NEGOTIATION', label: 'Negotiation' },
  { value: 'WON', label: 'Won — Enrolled' },
  { value: 'LOST', label: 'Lost' },
];

const emptyForm = {
  student: '',
  package: '',
  value: '',
  currency: 'USD',
  owner: '',
  expected_close_date: '',
  next_action_note: '',
  next_action_due: '',
};

const emptyQuoteLine = () => ({
  section: 'A', description: '', quantity: '1', unit_price: '0.00',
  tax_rate_pct: '0.00', price_item: '', paid_to: '',
});
const emptyQuoteForm = () => ({
  kind: 'INVOICE', currency: 'USD', subject: '', due_date: '', valid_until: '',
  arrival_date: '', terms: '', notes: '', lines: [emptyQuoteLine()],
});

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
};

const formatDueBadge = (iso) => {
  if (!iso) return null;
  const due = new Date(iso);
  const now = new Date();
  const overdue = due < now;
  return (
    <span className={`deal-due-badge ${overdue ? 'overdue' : ''}`}>
      <FiClock size={11} /> {due.toLocaleDateString()} {overdue ? '(overdue)' : ''}
    </span>
  );
};

// Shown after "Send for signature" / "Send quotation for acceptance" — one
// row per party with a copy-link button, a WhatsApp button when we have a
// phone on file, and whether the auto-email went out.
function SigningLinks({ links }) {
  const [copied, setCopied] = useState(null);
  if (!links || !links.length) return null;
  const copy = (url, id) => {
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied((c) => (c === id ? null : c)), 1500);
  };
  return (
    <div className="quotation-form" style={{ marginTop: 6 }}>
      <strong>Signing links</strong>
      {links.map((l) => (
        <div className="form-row" key={l.id} style={{ marginTop: 6, alignItems: 'center' }}>
          <span>{l.role_display}{l.expected_name ? ` — ${l.expected_name}` : ''}</span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => copy(l.sign_url, l.id)}>
            {copied === l.id ? 'Copied!' : 'Copy link'}
          </button>
          {l.whatsapp_url ? (
            <a className="btn-secondary btn-sm" href={l.whatsapp_url} target="_blank" rel="noreferrer"
              style={{ textAlign: 'center', textDecoration: 'none' }}>
              Open WhatsApp
            </a>
          ) : <span className="deals-no-history">No phone on file</span>}
          <span className="deals-no-history">
            {l.emailed ? (l.emailed.sent ? `Emailed: ${l.emailed.detail}` : l.emailed.detail) : 'No email sent'}
          </span>
        </div>
      ))}
    </div>
  );
}

const Deals = () => {
  const { isAnyStaff, isAgent, isFinanceLeadership, canIssueBillingDocs, canRecordPayments } = useAuth();
  // Founder rule, 19 Oct 2026: COO/CEO/CSM/Chairman/CFO (+ Admin) issue invoices and every other
  // billing document; only the CFO records a payment. Other staff draft from Billing Documents.
  const canBill = () => canIssueBillingDocs();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [agents, setAgents] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [selectedDeal, setSelectedDeal] = useState(null);
  const [detailTab, setDetailTab] = useState('details');
  const [quotations, setQuotations] = useState([]);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  const [quoteForm, setQuoteForm] = useState(emptyQuoteForm());
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [linkInvoiceInputs, setLinkInvoiceInputs] = useState({});
  const [priceItems, setPriceItems] = useState([]);
  const [myUser, setMyUser] = useState(null);
  const [signRole, setSignRole] = useState({});
  const [quoteError, setQuoteError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [editingQuoteId, setEditingQuoteId] = useState(null);

  useEffect(() => {
    billingAPI.priceItems.list({ is_active: true }).then((r) => setPriceItems(r.data?.results ?? r.data ?? [])).catch(() => {});
    usersAPI.me().then((r) => setMyUser(r.data)).catch(() => {});
  }, []);

  const downloadQuotePdf = async (q) => {
    setDownloadingPdf(q.id);
    setQuoteError('');
    try {
      const res = await quotationsAPI.pdf(q.id);
      downloadBlob(res, `${q.quote_number || 'quotation'}.pdf`);
    } catch (e) {
      setQuoteError('Could not generate the PDF.');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const signQuotation = async (q) => {
    const role = (signRole[q.id] || '').trim();
    if (!role) return;
    if (!myUser?.signature_file) {
      setQuoteError('No signature on file. Upload one in Settings → My signature first.');
      return;
    }
    const fd = new FormData();
    fd.append('role_label', role);
    try {
      await quotationsAPI.applySignature(q.id, fd);
      setSignRole((s) => ({ ...s, [q.id]: '' }));
      setQuoteError('');
      fetchQuotations(selectedDeal.id);
    } catch (err) {
      setQuoteError(err.response?.data ? JSON.stringify(err.response.data) : 'Could not apply signature.');
    }
  };

  const applyPriceItemToLine = (idx, itemId) => {
    const item = priceItems.find((p) => String(p.id) === String(itemId));
    if (!item) return;
    setQuoteForm((f) => {
      const lines = [...f.lines];
      lines[idx] = {
        ...lines[idx],
        price_item: item.id,
        description: item.description ? `${item.name} — ${item.description}` : item.name,
        unit_price: item.default_unit_price,
        tax_rate_pct: item.tax_rate_pct,
      };
      return { ...f, lines };
    });
  };

  const fetchDeals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await dealsAPI.list({ page_size: 500 });
      setDeals(res.data?.results ?? res.data ?? []);
    } catch (e) {
      console.error(e);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeals();
    studentsAPI.list({ page_size: 500 }).then((res) => {
      setStudents(res.data?.results ?? res.data ?? []);
    }).catch(() => setStudents([]));
    if (isAnyStaff()) {
      usersAPI.agents().then((res) => setAgents(res.data ?? [])).catch(() => setAgents([]));
    }
  }, [fetchDeals, isAnyStaff]);

  const columns = STAGES.map((s) => ({
    ...s,
    deals: deals.filter((d) => d.stage === s.value),
  }));

  const openCreate = () => {
    setForm(emptyForm);
    setFormError('');
    setShowCreateModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.student) {
      setFormError('Please select a student.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const payload = {
        ...form,
        value: form.value || '0',
        owner: form.owner || undefined,
        expected_close_date: form.expected_close_date || null,
        next_action_due: form.next_action_due ? new Date(form.next_action_due).toISOString() : null,
      };
      await dealsAPI.create(payload);
      setShowCreateModal(false);
      fetchDeals();
    } catch (err) {
      const msg = err.response?.data ? JSON.stringify(err.response.data) : 'Failed to create deal.';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoveStage = async (deal, newStage) => {
    if (newStage === deal.stage) return;
    let lostReason = '';
    if (newStage === 'LOST') {
      lostReason = window.prompt('Reason for marking this deal Lost?') || '';
      if (!lostReason.trim()) return;
    }
    try {
      await dealsAPI.moveStage(deal.id, newStage, '', lostReason);
      fetchDeals();
      if (selectedDeal?.id === deal.id) openDetail(deal.id);
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.[0] || 'Failed to move stage.');
    }
  };

  const openDetail = async (dealId) => {
    try {
      const res = await dealsAPI.get(dealId);
      setSelectedDeal(res.data);
      setDetailTab('details');
      // The Quotations tab is for document issuers (see canBill) — don't
      // fetch for anyone else.
      if (canBill()) {
        fetchQuotations(dealId);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchQuotations = async (dealId) => {
    setQuotationsLoading(true);
    try {
      const res = await quotationsAPI.list({ deal: dealId });
      setQuotations(res.data?.results ?? res.data ?? []);
    } catch (e) {
      setQuotations([]);
    } finally {
      setQuotationsLoading(false);
    }
  };

  const handleAddQuoteLine = () => {
    setQuoteForm((f) => ({ ...f, lines: [...f.lines, emptyQuoteLine()] }));
  };

  const handleQuoteLineChange = (idx, field, val) => {
    setQuoteForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => (i === idx ? { ...l, [field]: val } : l)),
    }));
  };

  const handleRemoveQuoteLine = (idx) => {
    setQuoteForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  };

  const startEditQuote = (q) => {
    setEditingQuoteId(q.id);
    setQuoteError('');
    setQuoteForm({
      kind: q.kind || 'INVOICE',
      currency: q.currency || 'USD',
      subject: q.subject || '',
      due_date: q.due_date || '',
      valid_until: q.valid_until || '',
      arrival_date: q.arrival_date || '',
      terms: q.terms || '',
      notes: q.notes || '',
      lines: (q.lines && q.lines.length ? q.lines : [emptyQuoteLine()]).map((l) => ({
        section: l.section || 'A',
        description: l.description || '',
        quantity: String(l.quantity ?? '1'),
        unit_price: String(l.unit_price ?? '0.00'),
        tax_rate_pct: String(l.tax_rate_pct ?? '0.00'),
        paid_to: l.paid_to || '',
        price_item: l.price_item || '',
      })),
    });
  };

  const cancelEditQuote = () => {
    setEditingQuoteId(null);
    setQuoteForm(emptyQuoteForm());
  };

  const handleCreateQuotation = async (e) => {
    e.preventDefault();
    if (!selectedDeal) return;
    setQuoteSubmitting(true);
    const payload = {
      deal: selectedDeal.id,
      student: selectedDeal.student,
      kind: quoteForm.kind,
      currency: quoteForm.currency,
      subject: quoteForm.subject,
      due_date: quoteForm.due_date || null,
      valid_until: quoteForm.valid_until || null,
      arrival_date: quoteForm.arrival_date || null,
      terms: quoteForm.terms,
      notes: quoteForm.notes,
      lines: quoteForm.lines
        .filter((l) => l.description.trim())
        .map((l, i) => ({
          section: l.section || 'A',
          order: i + 1,
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          tax_rate_pct: l.tax_rate_pct || '0.00',
          paid_to: l.section === 'B' ? (l.paid_to || '') : '',
          ...(l.price_item ? { price_item: l.price_item } : {}),
        })),
    };
    try {
      if (editingQuoteId) {
        await quotationsAPI.update(editingQuoteId, payload);
      } else {
        await quotationsAPI.create(payload);
      }
      cancelEditQuote();
      fetchQuotations(selectedDeal.id);
    } catch (err) {
      setQuoteError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save.');
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const handleQuoteAction = async (quotation, action) => {
    try {
      if (action === 'accepted') await quotationsAPI.markAccepted(quotation.id);
      if (action === 'declined') await quotationsAPI.markDeclined(quotation.id);
      fetchQuotations(selectedDeal.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Action failed.');
    }
  };

  // Quotations are sent by issuing them; invoices are confirmed & issued the
  // same way — once issued a document is frozen (corrections go through a
  // credit note or void + re-issue).
  const [actionBusy, setActionBusy] = useState(null);
  const [voidReason, setVoidReason] = useState({});
  const [payForm, setPayForm] = useState({});
  const [planBusyId, setPlanBusyId] = useState(null);

  // Credit / refund note — one or more lines, seeded from the invoice's own
  // Section A lines (only VisionExploit fees are ever credited) but editable
  // and extendable, since a credit rarely matches the invoice line-for-line.
  const [creditOpen, setCreditOpen] = useState({});
  const [creditReason, setCreditReason] = useState({});
  const [creditLines, setCreditLines] = useState({});

  const openCreditNote = (q) => {
    setCreditOpen((s) => ({ ...s, [q.id]: !s[q.id] }));
    if (!creditLines[q.id]) {
      const seeded = (q.lines || [])
        .filter((l) => l.section === 'A')
        .map((l) => ({ invoice_line: l.id, description: l.description, invoiced_amount: l.line_total, refund_pct: '100' }));
      setCreditLines((s) => ({ ...s, [q.id]: seeded.length ? seeded : [{ description: '', invoiced_amount: '', refund_pct: '100' }] }));
    }
  };

  const updateCreditLine = (qid, idx, field, val) => {
    setCreditLines((s) => ({
      ...s, [qid]: s[qid].map((l, i) => (i === idx ? { ...l, [field]: val } : l)),
    }));
  };

  const addCreditLine = (qid) => {
    setCreditLines((s) => ({ ...s, [qid]: [...(s[qid] || []), { description: '', invoiced_amount: '', refund_pct: '100' }] }));
  };

  const removeCreditLine = (qid, idx) => {
    setCreditLines((s) => ({ ...s, [qid]: s[qid].filter((_, i) => i !== idx) }));
  };

  const creditLineTotal = (qid) => (creditLines[qid] || []).reduce(
    (sum, l) => sum + (Number(l.invoiced_amount || 0) * Number(l.refund_pct || 0)) / 100, 0,
  );

  // Payment plan — the standard $30/50%/50% schedule ships with one click;
  // a custom schedule is a small editable instalment table underneath.
  const [planOpen, setPlanOpen] = useState({});
  const [planLines, setPlanLines] = useState({});

  const TRIGGER_OPTIONS = [
    ['ON_SIGNING', 'After signing the agreement'],
    ['ACCEPTANCE_LETTER', 'After the university acceptance letter'],
    ['VISA_GRANTED', 'After the visa is granted'],
    ['FIXED_DATE', 'On a fixed date'],
  ];
  const emptyPlanLine = () => ({ label: '', trigger: 'FIXED_DATE', offset_days: '7', fixed_date: '', amount: '' });

  const openCustomPlan = (q) => {
    setPlanOpen((s) => ({ ...s, [q.id]: !s[q.id] }));
    if (!planLines[q.id]) setPlanLines((s) => ({ ...s, [q.id]: [emptyPlanLine()] }));
  };

  const updatePlanLine = (qid, idx, field, val) => {
    setPlanLines((s) => ({ ...s, [qid]: s[qid].map((l, i) => (i === idx ? { ...l, [field]: val } : l)) }));
  };

  const addPlanLine = (qid) => setPlanLines((s) => ({ ...s, [qid]: [...(s[qid] || []), emptyPlanLine()] }));
  const removePlanLine = (qid, idx) => setPlanLines((s) => ({ ...s, [qid]: s[qid].filter((_, i) => i !== idx) }));
  const planLineTotal = (qid) => (planLines[qid] || []).reduce((sum, l) => sum + Number(l.amount || 0), 0);

  const issueQuote = async (q) => {
    setActionBusy(q.id);
    try {
      await quotationsAPI.issue(q.id);
      fetchQuotations(selectedDeal.id);
    } catch (err) {
      alert(err.response?.data?.detail || (err.response?.data ? JSON.stringify(err.response.data) : 'Could not issue.'));
    } finally {
      setActionBusy(null);
    }
  };

  const sendQuoteForSignature = async (q) => {
    setActionBusy(q.id);
    try {
      const r = await signingAPI.send('quotations', q.id);
      setQuoteLinks((s) => ({ ...s, [q.id]: r.data }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not create the acceptance link.');
    } finally {
      setActionBusy(null);
    }
  };

  const createInvoiceFromQuote = async (q) => {
    try {
      await quotationsAPI.createInvoice(q.id);
      fetchQuotations(selectedDeal.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not create an invoice from this quotation.');
    }
  };

  const voidQuote = async (q) => {
    const reason = (voidReason[q.id] || '').trim();
    if (!reason) return;
    setActionBusy(q.id);
    try {
      await quotationsAPI.void(q.id, reason);
      setVoidReason((s) => ({ ...s, [q.id]: '' }));
      fetchQuotations(selectedDeal.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not void.');
    } finally {
      setActionBusy(null);
    }
  };

  const recordPayment = async (q) => {
    const form = payForm[q.id] || {};
    if (!form.amount) return;
    setActionBusy(q.id);
    try {
      await quotationsAPI.recordPayment(q.id, {
        amount: form.amount, payment_method: form.method || 'CASH', payment_reference: form.reference || '',
      });
      setPayForm((s) => ({ ...s, [q.id]: { amount: '', method: 'CASH', reference: '' } }));
      fetchQuotations(selectedDeal.id);
    } catch (err) {
      alert(err.response?.data?.detail || (err.response?.data ? JSON.stringify(err.response.data) : 'Could not record the payment.'));
    } finally {
      setActionBusy(null);
    }
  };

  const [planLinks, setPlanLinks] = useState({});
  const [quoteLinks, setQuoteLinks] = useState({});

  const issueStandardPlan = async (q) => {
    setPlanBusyId(q.id);
    try {
      const r = await paymentPlansAPI.create({ invoice: q.id, use_standard_schedule: true });
      await paymentPlansAPI.issue(r.data.id);
      const sig = await signingAPI.send('payment-plans', r.data.id);
      setPlanLinks((s) => ({ ...s, [q.id]: sig.data }));
      const res = await paymentPlansAPI.pdf(r.data.id);
      downloadBlob(res, `${r.data.number || 'payment-plan'}.pdf`);
    } catch (err) {
      alert(err.response?.data?.detail || (err.response?.data ? JSON.stringify(err.response.data) : 'Could not create the payment plan.'));
    } finally {
      setPlanBusyId(null);
    }
  };

  const issueCustomPlan = async (q) => {
    const lines = (planLines[q.id] || []).filter((l) => l.label.trim() && l.amount);
    if (!lines.length) return;
    setPlanBusyId(q.id);
    try {
      const r = await paymentPlansAPI.create({
        invoice: q.id,
        instalments: lines.map((l, i) => ({
          order: i + 1, label: l.label, trigger: l.trigger,
          offset_days: l.trigger === 'FIXED_DATE' ? 0 : (l.offset_days || 0),
          fixed_date: l.trigger === 'FIXED_DATE' ? l.fixed_date : null,
          amount: l.amount,
        })),
      });
      await paymentPlansAPI.issue(r.data.id);
      setPlanOpen((s) => ({ ...s, [q.id]: false }));
      setPlanLines((s) => ({ ...s, [q.id]: [emptyPlanLine()] }));
      const sig = await signingAPI.send('payment-plans', r.data.id);
      setPlanLinks((s) => ({ ...s, [q.id]: sig.data }));
      const res = await paymentPlansAPI.pdf(r.data.id);
      downloadBlob(res, `${r.data.number || 'payment-plan'}.pdf`);
    } catch (err) {
      alert(err.response?.data?.detail || (err.response?.data ? JSON.stringify(err.response.data) : 'Could not create the payment plan.'));
    } finally {
      setPlanBusyId(null);
    }
  };

  const createCreditNote = async (q) => {
    const reason = (creditReason[q.id] || '').trim();
    const lines = (creditLines[q.id] || []).filter((l) => l.description.trim() && l.invoiced_amount);
    if (!reason || !lines.length) return;
    setActionBusy(q.id);
    try {
      const r = await creditNotesAPI.create({
        invoice: q.id, reason,
        lines: lines.map((l) => ({
          invoice_line: l.invoice_line || undefined, description: l.description,
          invoiced_amount: l.invoiced_amount, refund_pct: l.refund_pct || '100.00',
        })),
      });
      await creditNotesAPI.issue(r.data.id);
      setCreditOpen((s) => ({ ...s, [q.id]: false }));
      setCreditReason((s) => ({ ...s, [q.id]: '' }));
      setCreditLines((s) => ({ ...s, [q.id]: null }));
      fetchQuotations(selectedDeal.id);
      const res = await creditNotesAPI.pdf(r.data.id);
      downloadBlob(res, `${r.data.number || 'credit-note'}.pdf`);
    } catch (err) {
      alert(err.response?.data?.detail || (err.response?.data ? JSON.stringify(err.response.data) : 'Could not create the credit note.'));
    } finally {
      setActionBusy(null);
    }
  };

  const downloadStatement = async () => {
    if (!selectedDeal) return;
    const res = await statementsAPI.pdf(selectedDeal.student);
    downloadBlob(res, `statement-${selectedDeal.student}.pdf`);
  };

  const handleLinkInvoice = async (quotation) => {
    const invoiceId = linkInvoiceInputs[quotation.id];
    if (!invoiceId) return;
    try {
      await quotationsAPI.linkInvoice(quotation.id, invoiceId);
      fetchQuotations(selectedDeal.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not link that invoice. Check the invoice ID.');
    }
  };

  if (loading) {
    return <div className="loading">Loading deals…</div>;
  }

  return (
    <div className="deals-page">
      <div className="deals-header">
        <h3>Pipeline</h3>
        <button className="btn-primary" onClick={openCreate}>
          <FiPlus /> New Deal
        </button>
      </div>

      <div className="deals-board">
        {columns.map((col) => {
          const totalValue = col.deals.reduce((sum, d) => sum + Number(d.value || 0), 0);
          return (
            <div key={col.value} className={`deals-column deals-column-${col.value.toLowerCase()}`}>
              <div className="deals-column-header">
                <span className="deals-column-title">{col.label}</span>
                <span className="deals-column-meta">
                  {col.deals.length} · {totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
              <div className="deals-column-body">
                {col.deals.length === 0 ? (
                  <div className="deals-column-empty">No deals</div>
                ) : (
                  col.deals.map((deal) => (
                    <div key={deal.id} className="deal-card" onClick={() => openDetail(deal.id)}>
                      <div className="deal-card-title">{deal.title}</div>
                      <div className="deal-card-student">{deal.student_name}</div>
                      <div className="deal-card-value">
                        <FiDollarSign size={11} /> {Number(deal.value || 0).toLocaleString()} {deal.currency}
                      </div>
                      {deal.owner_name && (
                        <div className="deal-card-owner"><FiUser size={11} /> {deal.owner_name}</div>
                      )}
                      {deal.next_action_due && formatDueBadge(deal.next_action_due)}
                      <select
                        className="deal-stage-select"
                        value={deal.stage}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleMoveStage(deal, e.target.value)}
                      >
                        {STAGES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Deal modal */}
      {showCreateModal && (
        <div className="deals-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="deals-modal" onClick={(e) => e.stopPropagation()}>
            <h2>New Deal</h2>
            <form onSubmit={handleCreate}>
              {formError && <div className="error-message">{formError}</div>}
              <label>Student *</label>
              <select value={form.student} onChange={(e) => setForm({ ...form, student: e.target.value })} required>
                <option value="">Select a student</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>{s.full_name}</option>
                ))}
              </select>
              <label>Package / Service</label>
              <input
                type="text"
                value={form.package}
                onChange={(e) => setForm({ ...form, package: e.target.value })}
                placeholder="e.g. Premium Package"
              />
              <div className="form-row">
                <div className="form-group">
                  <label>Value</label>
                  <input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <input type="text" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
                </div>
              </div>
              {isAnyStaff() && agents.length > 0 && (
                <>
                  <label>Owner (agent)</label>
                  <select value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })}>
                    <option value="">Auto-assign</option>
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>{a.username}</option>
                    ))}
                  </select>
                </>
              )}
              <label>Expected close date</label>
              <input
                type="date"
                value={form.expected_close_date}
                onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })}
              />
              <label>Next action</label>
              <input
                type="text"
                value={form.next_action_note}
                onChange={(e) => setForm({ ...form, next_action_note: e.target.value })}
                placeholder="e.g. Send scholarship shortlist"
              />
              <label>Next action due</label>
              <input
                type="datetime-local"
                value={form.next_action_due}
                onChange={(e) => setForm({ ...form, next_action_due: e.target.value })}
              />
              <div className="deals-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving…' : 'Create Deal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deal detail modal */}
      {selectedDeal && (
        <div className="deals-modal-overlay" onClick={() => setSelectedDeal(null)}>
          <div className="deals-modal deals-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="deals-detail-header">
              <h2>{selectedDeal.title}</h2>
              <button className="deals-modal-close" onClick={() => setSelectedDeal(null)}><FiX /></button>
            </div>

            <div className="deals-detail-tabs">
              <button
                className={`deals-tab ${detailTab === 'details' ? 'active' : ''}`}
                onClick={() => setDetailTab('details')}
              >
                Details
              </button>
              {canBill() && (
                <button
                  className={`deals-tab ${detailTab === 'quotations' ? 'active' : ''}`}
                  onClick={() => setDetailTab('quotations')}
                >
                  Quotations ({quotations.length})
                </button>
              )}
            </div>

            {detailTab === 'details' ? (
              <div className="deals-detail-content">
                <div className="detail-item"><strong>Student:</strong> {selectedDeal.student_detail?.full_name}</div>
                <div className="detail-item"><strong>Stage:</strong> {selectedDeal.stage_display}</div>
                <div className="detail-item"><strong>Value:</strong> {selectedDeal.value} {selectedDeal.currency}</div>
                <div className="detail-item"><strong>Probability:</strong> {selectedDeal.probability}%</div>
                <div className="detail-item"><strong>Owner:</strong> {selectedDeal.owner_detail?.username || '—'}</div>
                <div className="detail-item"><strong>Expected close:</strong> {formatDate(selectedDeal.expected_close_date)}</div>
                {selectedDeal.next_action_note && (
                  <div className="detail-item"><strong>Next action:</strong> {selectedDeal.next_action_note}</div>
                )}
                {selectedDeal.next_action_due && (
                  <div className="detail-item"><strong>Next action due:</strong> {formatDate(selectedDeal.next_action_due)}</div>
                )}
                {selectedDeal.lost_reason && (
                  <div className="detail-item"><strong>Lost reason:</strong> {selectedDeal.lost_reason}</div>
                )}
                {!canBill() && (
                  <p className="deals-no-history">
                    Quotations and invoices for this deal are issued by the COO, CEO, CSM, Chairman or CFO.
                    Draft one from Billing Documents for them to issue.
                  </p>
                )}
                <h4>Stage history</h4>
                {(selectedDeal.stage_history || []).length === 0 ? (
                  <p className="deals-no-history">No stage changes yet.</p>
                ) : (
                  <ul className="deals-history-list">
                    {selectedDeal.stage_history.map((h) => (
                      <li key={h.id}>
                        <strong>{h.from_stage} → {h.to_stage}</strong>
                        {h.changed_by_name && ` · ${h.changed_by_name}`}
                        {' · '}{new Date(h.changed_at).toLocaleString()}
                        {h.note && <div className="deals-history-note">{h.note}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="deals-detail-content">
                {quotationsLoading ? (
                  <div className="deals-loading">Loading quotations…</div>
                ) : quotations.length === 0 ? (
                  <p className="deals-no-history">No quotations yet for this deal.</p>
                ) : (
                  quotations.map((q) => (
                    <div key={q.id} className="quotation-card">
                      <div className="quotation-card-header">
                        <strong>{q.quote_number || 'DRAFT'}</strong>
                        <span className="quotation-kind">{q.kind === 'INVOICE' ? 'Invoice' : 'Quotation'}</span>
                        <span className={`quotation-status quotation-status-${q.status.toLowerCase()}`}>{q.status}</span>
                        {q.kind === 'INVOICE' && q.payment_status && (
                          <span className={`quotation-status quotation-status-${q.payment_status.toLowerCase()}`}>
                            {q.payment_status.replace('_', '-')}
                          </span>
                        )}
                      </div>
                      <div className="quotation-dates">
                        Issued {q.issue_date ? new Date(`${q.issue_date}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        {q.kind === 'INVOICE' && q.due_date && <> · Due {new Date(`${q.due_date}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                        {q.kind === 'QUOTATION' && q.valid_until && <> · Valid until {new Date(`${q.valid_until}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                      </div>
                      {q.subject && <div className="quotation-subject">{q.subject}</div>}
                      <ul className="quotation-lines">
                        {q.lines.map((l) => (
                          <li key={l.id}>
                            <span className={`qline-sec qline-sec-${l.section}`}>{l.line_code || l.section}</span>{' '}
                            {l.description}
                            {l.section === 'B'
                              ? <> — {l.paid_to || '—'} · ≈ {l.line_total} {q.currency}</>
                              : <> — {l.quantity} × {l.unit_price} = {l.line_total} {q.currency}</>}
                          </li>
                        ))}
                      </ul>
                      <div className="quotation-total">
                        Service fees: {q.section_a_subtotal} {q.currency}
                        {Number(q.tax_total) > 0 && <> + tax {q.tax_total}</>}
                        {Number(q.section_b_subtotal) > 0 && <> · Third-party ≈ {q.section_b_subtotal}</>}
                        {' · '}<strong>Due: {q.amount_due} {q.currency}</strong>
                        {Number(q.section_b_subtotal) > 0 && <> · Est. total ≈ {q.estimated_total}</>}
                        {q.kind === 'INVOICE' && q.status === 'ISSUED' && (
                          <> · Paid: {q.paid_total} · Balance: <strong>{q.balance} {q.currency}</strong></>
                        )}
                      </div>
                      {q.status === 'DECLINED' && q.decline_info && (
                        <div className="quotation-declined">
                          Declined online{q.decline_info.by ? ` by ${q.decline_info.by}` : ''}
                          {q.decline_info.reason ? `: "${q.decline_info.reason}"` : ' (no reason given)'}
                        </div>
                      )}
                      {(q.signatures || []).length > 0 && (
                        <div className="quotation-linked">
                          Signed: {q.signatures.map((s) => `${s.role_label} (${s.signer_name})`).join(', ')}
                        </div>
                      )}
                      {q.linked_invoice_number && (
                        <div className="quotation-linked">Linked invoice: {q.linked_invoice_number}</div>
                      )}
                      {/* Agents never sign quotations — they go out through an employee/admin. */}
                      {!isAgent() && (
                        <div className="quotation-sign-row">
                          <input
                            type="text"
                            placeholder="Sign as… e.g. Prepared by — Finance"
                            value={signRole[q.id] || ''}
                            onChange={(e) => setSignRole((s) => ({ ...s, [q.id]: e.target.value }))}
                          />
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            disabled={!(signRole[q.id] || '').trim()}
                            onClick={() => signQuotation(q)}
                          >
                            <FiEdit3 size={12} /> Apply my signature
                          </button>
                        </div>
                      )}
                      {quoteError && <div className="quotation-error">{quoteError}</div>}
                      <div className="quotation-actions">
                        <button className="btn-secondary btn-sm" disabled={downloadingPdf === q.id} onClick={() => downloadQuotePdf(q)}>
                          <FiDownload size={12} /> {downloadingPdf === q.id ? 'Preparing…' : 'PDF'}
                        </button>
                        {!q.is_locked && (
                          <button
                            className={`btn-secondary btn-sm ${editingQuoteId === q.id ? 'active' : ''}`}
                            onClick={() => (editingQuoteId === q.id ? cancelEditQuote() : startEditQuote(q))}
                          >
                            <FiEdit3 size={12} /> {editingQuoteId === q.id ? 'Editing…' : 'Edit amounts'}
                          </button>
                        )}
                        {q.status === 'DRAFT' && q.kind === 'QUOTATION' && (
                          <button className="btn-secondary btn-sm" disabled={actionBusy === q.id} onClick={() => issueQuote(q)}>
                            {actionBusy === q.id ? 'Sending…' : 'Send quotation'}
                          </button>
                        )}
                        {q.status === 'DRAFT' && q.kind === 'INVOICE' && canBill() && (
                          <button className="btn-primary btn-sm" disabled={actionBusy === q.id} onClick={() => issueQuote(q)}>
                            {actionBusy === q.id ? 'Issuing…' : 'Confirm & issue invoice'}
                          </button>
                        )}
                        {q.status === 'SENT' && q.kind === 'QUOTATION' && (
                          <>
                            <button className="btn-secondary btn-sm" disabled={actionBusy === q.id} onClick={() => sendQuoteForSignature(q)}>
                              {actionBusy === q.id ? 'Creating…' : 'Send for client acceptance'}
                            </button>
                            <button className="btn-primary btn-sm" onClick={() => handleQuoteAction(q, 'accepted')}>Mark Accepted (in person)</button>
                            <button className="btn-secondary btn-sm" onClick={() => handleQuoteAction(q, 'declined')}>Mark Declined</button>
                          </>
                        )}
                        {q.status === 'ACCEPTED' && q.kind === 'QUOTATION' && (
                          <button className="btn-primary btn-sm" onClick={() => createInvoiceFromQuote(q)}>
                            Create invoice from this quote
                          </button>
                        )}
                        {q.status === 'ACCEPTED' && q.kind === 'QUOTATION' && !q.linked_invoice && (
                          <span className="quotation-link-invoice">
                            <input
                              type="number"
                              placeholder="Finance invoice ID"
                              value={linkInvoiceInputs[q.id] || ''}
                              onChange={(e) => setLinkInvoiceInputs({ ...linkInvoiceInputs, [q.id]: e.target.value })}
                            />
                            <button className="btn-secondary btn-sm" onClick={() => handleLinkInvoice(q)}>Link invoice</button>
                          </span>
                        )}
                      </div>

                      {quoteLinks[q.id] && <SigningLinks links={quoteLinks[q.id]} />}

                      {q.kind === 'INVOICE' && q.status === 'ISSUED' && q.balance > 0 && !canRecordPayments() && (
                        <p className="deals-no-history">
                          Balance {q.balance} {q.currency} outstanding. Only the CFO or Chairman records payments.
                        </p>
                      )}
                      {q.kind === 'INVOICE' && q.status === 'ISSUED' && q.balance > 0 && canRecordPayments() && (
                        <div className="quotation-sign-row">
                          <input
                            type="number" step="0.01" placeholder={`Amount (≤ ${q.balance})`}
                            value={(payForm[q.id] || {}).amount || ''}
                            onChange={(e) => setPayForm((s) => ({ ...s, [q.id]: { ...s[q.id], amount: e.target.value } }))}
                            style={{ maxWidth: 120 }}
                          />
                          <select
                            value={(payForm[q.id] || {}).method || 'CASH'}
                            onChange={(e) => setPayForm((s) => ({ ...s, [q.id]: { ...s[q.id], method: e.target.value } }))}
                          >
                            {['CASH', 'BANK_TRANSFER', 'CARD', 'ONLINE', 'OTHER'].map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <input
                            type="text" placeholder="Reference (optional)"
                            value={(payForm[q.id] || {}).reference || ''}
                            onChange={(e) => setPayForm((s) => ({ ...s, [q.id]: { ...s[q.id], reference: e.target.value } }))}
                          />
                          <button
                            type="button" className="btn-primary btn-sm" disabled={actionBusy === q.id || !(payForm[q.id] || {}).amount}
                            onClick={() => recordPayment(q)}
                          >
                            {actionBusy === q.id ? 'Recording…' : 'Record payment'}
                          </button>
                        </div>
                      )}

                      {q.kind === 'INVOICE' && q.status === 'ISSUED' && (
                        <div className="quotation-actions" style={{ flexWrap: 'wrap' }}>
                          <button type="button" className="btn-secondary btn-sm" disabled={planBusyId === q.id} onClick={() => issueStandardPlan(q)}>
                            {planBusyId === q.id ? 'Creating…' : 'Issue standard payment plan'}
                          </button>
                          <button type="button" className={`btn-secondary btn-sm ${planOpen[q.id] ? 'active' : ''}`} onClick={() => openCustomPlan(q)}>
                            {planOpen[q.id] ? 'Cancel custom plan' : 'Custom payment plan…'}
                          </button>
                        </div>
                      )}

                      {planOpen[q.id] && (
                        <div className="quotation-form" style={{ marginTop: 6 }}>
                          {(planLines[q.id] || []).map((l, idx) => (
                            <div className="form-row quotation-line-row" key={idx}>
                              <input
                                type="text" placeholder="Label, e.g. Deposit"
                                value={l.label} onChange={(e) => updatePlanLine(q.id, idx, 'label', e.target.value)}
                              />
                              <select value={l.trigger} onChange={(e) => updatePlanLine(q.id, idx, 'trigger', e.target.value)}>
                                {TRIGGER_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                              </select>
                              {l.trigger === 'FIXED_DATE' ? (
                                <input
                                  type="date" value={l.fixed_date}
                                  onChange={(e) => updatePlanLine(q.id, idx, 'fixed_date', e.target.value)}
                                />
                              ) : (
                                <input
                                  type="number" placeholder="Days after" style={{ maxWidth: 90 }}
                                  value={l.offset_days} onChange={(e) => updatePlanLine(q.id, idx, 'offset_days', e.target.value)}
                                />
                              )}
                              <input
                                type="number" step="0.01" placeholder="Amount" style={{ maxWidth: 100 }}
                                value={l.amount} onChange={(e) => updatePlanLine(q.id, idx, 'amount', e.target.value)}
                              />
                              {(planLines[q.id] || []).length > 1 && (
                                <button type="button" className="quotation-line-remove" onClick={() => removePlanLine(q.id, idx)}>
                                  <FiTrash2 size={13} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button type="button" className="btn-secondary btn-sm" onClick={() => addPlanLine(q.id)}>+ Add instalment</button>
                          <div className="deals-modal-actions" style={{ marginTop: 8 }}>
                            <span className="deals-no-history">
                              Total: {planLineTotal(q.id).toFixed(2)} {q.currency}
                              {Math.abs(planLineTotal(q.id) - Number(q.balance)) > 0.005 && (
                                <> — must equal the balance ({q.balance} {q.currency})</>
                              )}
                            </span>
                          </div>
                          <div className="deals-modal-actions">
                            <button
                              type="button" className="btn-primary btn-sm" disabled={planBusyId === q.id}
                              onClick={() => issueCustomPlan(q)}
                            >
                              {planBusyId === q.id ? 'Issuing…' : 'Issue this plan'}
                            </button>
                          </div>
                        </div>
                      )}

                      {planLinks[q.id] && <SigningLinks links={planLinks[q.id]} />}

                      {q.kind === 'INVOICE' && q.status === 'ISSUED' && isFinanceLeadership() && (
                        <div className="quotation-actions">
                          <button type="button" className={`btn-secondary btn-sm ${creditOpen[q.id] ? 'active' : ''}`} onClick={() => openCreditNote(q)}>
                            {creditOpen[q.id] ? 'Cancel credit note' : 'Credit / refund note…'}
                          </button>
                        </div>
                      )}

                      {creditOpen[q.id] && (
                        <div className="quotation-form" style={{ marginTop: 6 }}>
                          <input
                            type="text" placeholder="Reason for the credit / refund (printed on the note)"
                            value={creditReason[q.id] || ''}
                            onChange={(e) => setCreditReason((s) => ({ ...s, [q.id]: e.target.value }))}
                            style={{ width: '100%', marginBottom: 8 }}
                          />
                          {(creditLines[q.id] || []).map((l, idx) => (
                            <div className="form-row quotation-line-row" key={idx}>
                              <input
                                type="text" placeholder="Description"
                                value={l.description} onChange={(e) => updateCreditLine(q.id, idx, 'description', e.target.value)}
                              />
                              <input
                                type="number" step="0.01" placeholder="Invoiced amount" style={{ maxWidth: 110 }}
                                value={l.invoiced_amount} onChange={(e) => updateCreditLine(q.id, idx, 'invoiced_amount', e.target.value)}
                              />
                              <input
                                type="number" placeholder="Refund %" style={{ maxWidth: 80 }}
                                value={l.refund_pct} onChange={(e) => updateCreditLine(q.id, idx, 'refund_pct', e.target.value)}
                              />
                              {(creditLines[q.id] || []).length > 1 && (
                                <button type="button" className="quotation-line-remove" onClick={() => removeCreditLine(q.id, idx)}>
                                  <FiTrash2 size={13} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button type="button" className="btn-secondary btn-sm" onClick={() => addCreditLine(q.id)}>+ Add line</button>
                          <div className="deals-modal-actions" style={{ marginTop: 8 }}>
                            <span className="deals-no-history">Credit total: {creditLineTotal(q.id).toFixed(2)} {q.currency}</span>
                          </div>
                          <div className="deals-modal-actions">
                            <button
                              type="button" className="btn-primary btn-sm"
                              disabled={actionBusy === q.id || !(creditReason[q.id] || '').trim()}
                              onClick={() => createCreditNote(q)}
                            >
                              {actionBusy === q.id ? 'Issuing…' : 'Issue credit / refund note'}
                            </button>
                          </div>
                        </div>
                      )}

                      {q.status === 'ISSUED' && isFinanceLeadership() && (
                        <div className="quotation-sign-row">
                          <input
                            type="text" placeholder="Reason to void"
                            value={voidReason[q.id] || ''}
                            onChange={(e) => setVoidReason((s) => ({ ...s, [q.id]: e.target.value }))}
                          />
                          <button
                            type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }}
                            disabled={actionBusy === q.id || !(voidReason[q.id] || '').trim()}
                            onClick={() => voidQuote(q)}
                          >
                            Void
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
                {selectedDeal && (
                  <button type="button" className="btn-secondary btn-sm" onClick={downloadStatement} style={{ marginBottom: 12 }}>
                    <FiDownload size={12} /> Statement of account
                  </button>
                )}

                <h4>{editingQuoteId ? 'Edit quotation / invoice' : 'New quotation / invoice'}</h4>
                <form onSubmit={handleCreateQuotation} className="quotation-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Document</label>
                      <select value={quoteForm.kind} onChange={(e) => setQuoteForm({ ...quoteForm, kind: e.target.value })}>
                        <option value="QUOTATION">Quotation</option>
                        <option value="INVOICE">Invoice</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Currency</label>
                      <input type="text" value={quoteForm.currency} onChange={(e) => setQuoteForm({ ...quoteForm, currency: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>{quoteForm.kind === 'INVOICE' ? 'Due date' : 'Valid until'}</label>
                      <input
                        type="date"
                        value={quoteForm.kind === 'INVOICE' ? quoteForm.due_date : quoteForm.valid_until}
                        onChange={(e) => setQuoteForm({
                          ...quoteForm,
                          [quoteForm.kind === 'INVOICE' ? 'due_date' : 'valid_until']: e.target.value,
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Arrival date</label>
                      <input type="date" value={quoteForm.arrival_date} onChange={(e) => setQuoteForm({ ...quoteForm, arrival_date: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Re: (subject)</label>
                    <input
                      type="text"
                      placeholder="e.g. Arrival & Settlement Package — airport pickup, accommodation setup, residence-permit assistance"
                      value={quoteForm.subject}
                      onChange={(e) => setQuoteForm({ ...quoteForm, subject: e.target.value })}
                    />
                  </div>
                  {priceItems.length > 0 && (
                    <p className="deals-no-history" style={{ margin: '4px 0' }}>
                      Section A = VisionExploit fees (collected). Section B = third-party estimates (not collected).
                      Pick a service from the price book to fill a line.
                    </p>
                  )}
                  {quoteForm.lines.map((line, idx) => (
                    <div className="form-row quotation-line-row" key={idx}>
                      <select
                        value={line.section}
                        onChange={(e) => handleQuoteLineChange(idx, 'section', e.target.value)}
                        style={{ maxWidth: 64 }}
                        title="Section"
                      >
                        <option value="A">A</option>
                        <option value="B">B</option>
                      </select>
                      {priceItems.length > 0 && line.section === 'A' && (
                        <select
                          value={line.price_item || ''}
                          onChange={(e) => applyPriceItemToLine(idx, e.target.value)}
                          style={{ maxWidth: 130 }}
                        >
                          <option value="">From price book…</option>
                          {priceItems.map((p) => (
                            <option key={p.id} value={p.id}>{p.name} ({p.currency} {p.default_unit_price})</option>
                          ))}
                        </select>
                      )}
                      <input
                        type="text"
                        placeholder="Description"
                        value={line.description}
                        onChange={(e) => handleQuoteLineChange(idx, 'description', e.target.value)}
                      />
                      {line.section === 'B' ? (
                        <input
                          type="text"
                          placeholder="Paid to (third party)"
                          value={line.paid_to}
                          onChange={(e) => handleQuoteLineChange(idx, 'paid_to', e.target.value)}
                        />
                      ) : (
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Qty"
                          value={line.quantity}
                          onChange={(e) => handleQuoteLineChange(idx, 'quantity', e.target.value)}
                          style={{ maxWidth: 70 }}
                        />
                      )}
                      <input
                        type="number"
                        step="0.01"
                        placeholder={line.section === 'B' ? 'Est. amount' : 'Unit price'}
                        value={line.unit_price}
                        onChange={(e) => handleQuoteLineChange(idx, 'unit_price', e.target.value)}
                      />
                      {quoteForm.lines.length > 1 && (
                        <button type="button" className="quotation-line-remove" onClick={() => handleRemoveQuoteLine(idx)}>
                          <FiTrash2 size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn-secondary btn-sm" onClick={handleAddQuoteLine}>+ Add line</button>
                  <div className="form-group" style={{ marginTop: 10 }}>
                    <label>Notes &amp; terms (leave blank to use the template default)</label>
                    <textarea
                      rows={3}
                      value={quoteForm.terms}
                      onChange={(e) => setQuoteForm({ ...quoteForm, terms: e.target.value })}
                      placeholder="One term per line. Lines starting with '- ' render as bullets."
                    />
                  </div>
                  <div className="deals-modal-actions">
                    <button type="submit" className="btn-primary" disabled={quoteSubmitting}>
                      {quoteSubmitting
                        ? 'Saving…'
                        : editingQuoteId
                          ? 'Save changes'
                          : `Create ${quoteForm.kind === 'INVOICE' ? 'Invoice' : 'Quotation'}`}
                    </button>
                    {editingQuoteId && (
                      <button type="button" className="btn-secondary" onClick={cancelEditQuote}>Cancel</button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Deals;
